<template>
  <v-dialog
    :value="value"
    max-width="600"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="headline">
        <v-icon
          left
          color="primary"
        >
          mdi-card-plus
        </v-icon>
        {{ dialogTitle }}
        <v-spacer />
        <v-btn
          icon
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-alert
          type="info"
          dense
          text
          class="mb-4"
        >
          {{ helperMessage }}
        </v-alert>

        <v-form
          ref="form"
          lazy-validation
        >
          <v-autocomplete
            v-model="codeInput"
            :items="availableGiftCardSuggestions"
            :search-input.sync="searchInput"
            :label="$t($ts.giftCardDialogs.code)"
            prepend-inner-icon="mdi-pound"
            :disabled="isLoading"
            :loading="loadingSuggestions"
            outlined
            dense
            hide-details="auto"
            clearable
            no-filter
            @keydown.enter.prevent="handleAddCode"
            @change="handleCodeSelected"
          >
            <template #item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template #append-outer>
              <v-btn
                icon
                small
                @click="toggleScanner"
              >
                <v-icon>{{ showScanner ? "mdi-camera-off" : "mdi-qrcode-scan" }}</v-icon>
              </v-btn>
            </template>
          </v-autocomplete>
        </v-form>

        <v-expand-transition>
          <div
            v-if="showScanner"
            class="mt-4"
          >
            <v-alert
              type="info"
              dense
              text
              class="mb-2"
            >
              {{ $t($ts.giftCardDialogs.scanHelpSale) }}
            </v-alert>
            <qrcode-stream
              @decode="handleScan"
              @init="handleScannerInit"
            />
          </div>
        </v-expand-transition>

        <v-progress-linear
          v-if="isLoading"
          indeterminate
          color="primary"
          class="mt-4"
        />

        <v-list
          v-if="selectedCards.length"
          two-line
          class="mt-4"
        >
          <v-subheader>{{ $t($ts.giftCardDialogs.selectedCards) }}</v-subheader>
          <v-divider />
          <template v-for="card in selectedCards">
            <v-list-item :key="card.code">
              <v-list-item-content>
                <v-list-item-title>
                  {{ card.code }}
                  <v-chip
                    x-small
                    :color="statusColor(card.status)"
                    text-color="white"
                    class="ml-2"
                  >
                    {{ statusLabel(card.status) }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span>{{ card.giftCardType?.name || card.notes || $t($ts.giftCardDialogs.unnamed) }}</span>
                  ·
                  <strong>{{ formatCurrency(card.giftCardType?.value ?? card.initialValue, card.currency) }}</strong>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  icon
                  @click="removeCard(card.code)"
                >
                  <v-icon color="error">
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider />
          </template>
        </v-list>

        <v-alert
          v-else
          type="info"
          text
          class="mt-4"
        >
          {{ $t($ts.giftCardDialogs.noneAdded) }}
        </v-alert>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          text
          @click="close"
        >
          {{ $t($ts.giftCardDialogs.cancel) }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!selectedCards.length"
          @click="confirmSelection"
        >
          <v-icon left>
            mdi-check
          </v-icon>
          {{ $t($ts.giftCardDialogs.add) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { GiftCardStatus, IGiftCardDTO } from "@shared/types";
import { giftCardsModule } from "@/store/modules/giftCards/giftCardsModule";
import { snackbarModule } from "@/store/modules/snackbar/snackbarModule";

@Component({
  name: "gift-card-sale-dialog",
})
export default class GiftCardSaleDialog extends Vue {
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: Array, default: () => [GiftCardStatus.Generated, GiftCardStatus.Printed] }) readonly allowedStatuses!: GiftCardStatus[];
  @Prop({ type: Boolean, default: true }) readonly allowMultiple!: boolean;
  @Prop({ type: String, default: "" }) readonly titleOverride!: string;
  @Prop({ type: String, default: "" }) readonly infoMessage!: string;
  /** When true, cards with a zero balance are neither suggested nor accepted (used for redemption). */
  @Prop({ type: Boolean, default: false }) readonly requirePositiveBalance!: boolean;
  /** Only cards of this type (the gift card type tapped in the point of sale). */
  @Prop({ type: Number, default: null }) readonly giftCardTypeId!: number | null;
  /** Codes already chosen elsewhere (the cart): not suggested and not accepted again. */
  @Prop({ type: Array, default: () => [] }) readonly excludedCodes!: string[];

  public codeInput = "";
  public searchInput = "";
  public showScanner = false;
  public isLoading = false;
  public loadingSuggestions = false;
  public selectedCards: IGiftCardDTO[] = [];
  public availableCards: IGiftCardDTO[] = [];

  get dialogTitle(): string {
    return this.titleOverride || this.$t(this.$ts.giftCardDialogs.saleTitle).toString();
  }

  get helperMessage(): string {
    return this.infoMessage || this.$t(this.$ts.giftCardDialogs.saleHelp).toString();
  }

  get GiftCardStatus() {
    return GiftCardStatus;
  }

  isCardAllowed(card: IGiftCardDTO): boolean {
    if (!this.allowedStatuses.includes(card.status)) {
      return false;
    }
    return !this.requirePositiveBalance || (card.balance ?? 0) > 0;
  }

  get availableGiftCardSuggestions() {
    const searchTerm = (this.searchInput || "").toLowerCase();

    return this.availableCards
      .filter(card =>
        this.isCardAllowed(card) &&
        !this.selectedCards.some(c => c.code === card.code) &&
        !this.excludedCodes.includes(card.code) &&
        (!this.giftCardTypeId || card.giftCardTypeId === this.giftCardTypeId) &&
        (card.code.toLowerCase().includes(searchTerm) ||
         card.giftCardType?.name.toLowerCase().includes(searchTerm))
      )
      .map(card => ({
        text: card.code,
        value: card.code,
        subtitle: `${card.giftCardType?.name || this.$t(this.$ts.giftCardDialogs.giftCard)} - ${this.formatCurrency(card.giftCardType?.value ?? card.initialValue, card.currency)}`,
      }));
  }

  async mounted() {
    await this.loadAvailableCards();
  }

  async loadAvailableCards() {
    this.loadingSuggestions = true;
    try {
      await giftCardsModule.fetchCards();
      this.availableCards = giftCardsModule.cards.filter(card => this.isCardAllowed(card));
    } finally {
      this.loadingSuggestions = false;
    }
  }

  handleCodeSelected(code: string) {
    if (code) {
      this.handleAddCode();
    }
  }

  async handleAddCode(): Promise<void> {
    if (!this.codeInput.trim()) return;
    await this.addCode(this.codeInput.trim());
    this.codeInput = "";
  }

  public async addCode(rawCode: string): Promise<void> {
    const normalized = this.extractCode(rawCode);
    if (!normalized) {
      snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.invalidCode).toString());
      return;
    }
    if (this.selectedCards.some((c) => c.code === normalized) || this.excludedCodes.includes(normalized)) {
      snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.alreadyAdded).toString());
      return;
    }

    this.isLoading = true;
    try {
      const card = await giftCardsModule.lookupByCode(normalized);
      if (!card) {
        return;
      }
      if (!this.isCardAllowed(card)) {
        snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.notAvailable).toString());
        return;
      }
      if (this.allowMultiple) {
        this.selectedCards.push(card);
      } else {
        this.selectedCards = [card];
      }
    } finally {
      this.isLoading = false;
    }
  }

  public removeCard(code: string): void {
    this.selectedCards = this.selectedCards.filter((card) => card.code !== code);
  }

  public toggleScanner(): void {
    this.showScanner = !this.showScanner;
  }

  public handleScannerInit(promise: Promise<void>): void {
    promise.catch(() => {
      snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.cameraUnavailable).toString());
      this.showScanner = false;
    });
  }

  public async handleScan(content: string): Promise<void> {
    this.showScanner = false;
    await this.addCode(content);
  }

  public extractCode(value: string): string {
    try {
      const maybeUrl = new URL(value);
      const codeParam = maybeUrl.searchParams.get("code");
      return codeParam ? codeParam.toUpperCase() : maybeUrl.pathname.replace("/redeem", "").split("/").pop()?.toUpperCase() || "";
    } catch (e) {
      return value.replace(/\s+/g, "").toUpperCase();
    }
  }

  public statusLabel(status: GiftCardStatus): string {
    const statuses = this.$ts.giftCardManagement.cards.statuses;
    const keyMap: Record<GiftCardStatus, string> = {
      [GiftCardStatus.Generated]: statuses.generated,
      [GiftCardStatus.Printed]: statuses.printed,
      [GiftCardStatus.Active]: statuses.active,
      [GiftCardStatus.Redeemed]: statuses.redeemed,
      [GiftCardStatus.PartiallyRedeemed]: statuses.partiallyRedeemed,
      [GiftCardStatus.Expired]: statuses.expired,
      [GiftCardStatus.Voided]: statuses.voided,
    };
    return keyMap[status] ? this.$t(keyMap[status]).toString() : "";
  }

  public statusColor(status: GiftCardStatus): string {
    switch (status) {
      case GiftCardStatus.Generated:
      case GiftCardStatus.Printed:
        return "blue";
      case GiftCardStatus.Active:
        return "green";
      case GiftCardStatus.Redeemed:
        return "success";
      case GiftCardStatus.Expired:
      case GiftCardStatus.Voided:
        return "grey";
      default:
        return "primary";
    }
  }

  public formatCurrency(amount: number | undefined, currency: string | undefined): string {
    if (amount === undefined) return "-";
    const formatter = new Intl.NumberFormat(this.$i18n.locale, {
      style: "currency",
      currency: currency || "NOK",
    });
    return formatter.format(amount);
  }

  public confirmSelection(): void {
    this.$emit("confirm", this.selectedCards.slice());
    this.close();
  }

  public close(): void {
    this.codeInput = "";
    this.selectedCards = [];
    this.showScanner = false;
    this.$emit("input", false);
  }
}
</script>

<style scoped lang="scss">
.v-alert {
  border-radius: 8px;
}
</style>
